import { OverlayTrigger, Tooltip } from "react-bootstrap";
import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Grid } from "../../../../components";
import ClassificationIcon from "../../../../components/ClassificationIcon";
import { usePagination } from "../../../../hooks";
import { PAGE_PATH } from "../../../../path";
import { createSelectedRowKey } from "../../util";
import SampleReportGridLink from "../details/SampleReportGrid/SampleReportGridLink";
import SummaryBlock from "./SummaryBlock";
import YaraStringMatchLink from "./YaraStringMatchLink";
import TooltipWrapper from "../../../../enhancers/TooltipWrapper";
import styles from "../report_summary/cards/yara_matches/yara-matches.module.scss";

export const SUMMARY_YARA_MATCHES_ID = "summary-yara-matches";

const DEFAULT_SAMPLE_SUMMARY_PAGING = {
  page: 1,
  pageSize: 5
};

const DEFAULT_SAMPLE_SUMMARY_PAGE_SIZES = [
  { label: "5", value: "5" },
  { label: "10", value: "10" },
  { label: "20", value: "20" },
  { label: "100", value: "100" }
];

const SummaryYaraMatches = ({ data, ...props }) => {
  const grid = {
    fields: [
      {
        name: "file",
        displayTitle: "File",
        component: itemData => (
          <div className={"py-5"}>
            <SampleReportGridLink
              data={itemData}
              section="ticore/classification/yara"
              {...props}
            />
          </div>
        )
      },
      {
        name: "ruleset",
        displayTitle: "Ruleset",
        component: itemData => (
          <div>
            {itemData?.rulesets?.map((ruleset, i) => {
              const yaraDetailsRoute =
                PAGE_PATH.YARA +
                PAGE_PATH.YARA_DETAILS.replace(
                  ":rulesetId",
                  ruleset.revision_id
                );

              return (
                <div key={`${ruleset.name}-${i}`}>
                  <div className={"overflow-ellipsis py-5"}>
                    <TooltipWrapper
                      condition={!ruleset.revision_id}
                      wrapper={children => (
                        <OverlayTrigger
                          overlay={
                            <Tooltip>
                              Ruleset was deleted from the system.
                            </Tooltip>
                          }
                          placement="top"
                        >
                          <span>{children}</span>
                        </OverlayTrigger>
                      )}
                    >
                      {ruleset.revision_id ? (
                        <Link to={yaraDetailsRoute}>
                          {ruleset.name ?? "--"}
                        </Link>
                      ) : (
                        <span
                          className={styles.item_link}
                          style={{ textDecoration: "none" }}
                        >
                          {ruleset.name ?? "--"}
                        </span>
                      )}
                    </TooltipWrapper>
                  </div>
                  {Array.from({ length: ruleset.rules?.length - 1 ?? 0 }).map(
                    (_, j) => (
                      <div key={j} className={"py-5"}>
                        &nbsp;
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )
      },
      {
        name: "rules",
        displayTitle: "Rules",
        component: itemData => (
          <div>
            {itemData?.rulesets
              ?.map(ruleset => ruleset.rules)
              .flat()
              .map((rule, i) => (
                <div
                  key={`${rule.name}-${i}`}
                  className={"overflow-ellipsis py-5"}
                >
                  <span className="sample-report-grid__threat-icon-container">
                    <ClassificationIcon classification={rule.classname} />
                  </span>
                  {rule?.name ?? "--"}
                </div>
              ))}
          </div>
        )
      },
      {
        name: "matches",
        displayTitle: "Matches",
        component: itemData => (
          <div>
            {itemData?.rulesets
              ?.map(ruleset =>
                ruleset.rules.map(rule => ({
                  ...rule,
                  rulesetName: ruleset.name
                }))
              )
              .flat()
              .map((rule, i) => (
                <div key={`${rule.name}-${i}`} className={"py-5"}>
                  <YaraStringMatchLink
                    rule={rule}
                    stringMatchCount={rule?.string_match_count}
                    sha1={itemData.sha1}
                    sourceId={props.sampleSourceID}
                    relationshipId={
                      props.isExtractedFile && data.relationship_id === 0
                        ? props.relationshipID
                        : data.relationship_id
                    }
                    initialRulesetName={rule.rulesetName}
                    initialRuleName={rule.name}
                  />
                </div>
              ))}
          </div>
        )
      }
    ]
  };

  const {
    currentPageData,
    data: page,
    updatePageData
  } = usePagination(data ?? [], DEFAULT_SAMPLE_SUMMARY_PAGING);

  return (
    <SummaryBlock
      id={SUMMARY_YARA_MATCHES_ID}
      count={data.length}
      title="YARA Matches"
      icon="yara-with-arrows"
    >
      <Grid
        schema={grid}
        data={page}
        numPages={currentPageData.numPages}
        page={currentPageData.page}
        pageSize={currentPageData.pageSize}
        totalResults={data?.length || 0}
        onPageChange={(value, pageSize) => {
          updatePageData({ newPage: value, newPageSize: pageSize });
        }}
        enablePageSizeSelector
        showPageNumbers
        pageSizeSelectorItems={DEFAULT_SAMPLE_SUMMARY_PAGE_SIZES}
        tableProps={{
          className: "table-striped-sample-summary"
        }}
        createSelectedRowKey={createSelectedRowKey}
      />
    </SummaryBlock>
  );
};

SummaryYaraMatches.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      filename: PropTypes.string,
      relationship_id: PropTypes.number,
      rulesets: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          rules: PropTypes.arrayOf(
            PropTypes.shape({
              classname: PropTypes.string,
              name: PropTypes.string,
              string_match_count: PropTypes.number
            })
          )
        })
      ),
      sha1: PropTypes.string
    })
  ),
  sampleSourceID: PropTypes.number,
  relationshipID: PropTypes.number,
  isExtractedFile: PropTypes.bool
};

export default SummaryYaraMatches;
